/**
 * Billing Domain Seed Data
 *
 * Contains all seed data for the Billing Service (26):
 * - Subscription Plans
 * - Subscriptions
 * - Usage Metrics
 * - Payment Methods
 * - Invoices and Invoice Items
 * - Payments
 * - Billing Cycles
 *
 * @see 26-billing-service/prisma/schema.prisma
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// PLAN IDs
// =============================================================================

export const PLAN_IDS = {
  FREE: seedId('plan-free', 1),
  STARTER: seedId('plan-starter', 2),
  PROFESSIONAL: seedId('plan-prof', 3),
  ENTERPRISE: seedId('plan-ent', 4),
} as const;

export type PlanId = (typeof PLAN_IDS)[keyof typeof PLAN_IDS];

// =============================================================================
// SUBSCRIPTION IDs
// =============================================================================

export const SUBSCRIPTION_IDS = {
  OPSMATRIX_CAPITAL: seedId('sub-opsmatrix', 1),
  ONE_SHARPE_CAPITAL: seedId('sub-1sharpe', 2),
  PACIFIC_BRIDGE_FUND: seedId('sub-pacific', 3),
  GULF_COAST_FO: seedId('sub-gulfcoast', 4),
} as const;

export type SubscriptionId = (typeof SUBSCRIPTION_IDS)[keyof typeof SUBSCRIPTION_IDS];

// =============================================================================
// METRIC IDs
// =============================================================================

export const METRIC_IDS = {
  API_CALLS: seedId('metric-api', 1),
  STORAGE_GB: seedId('metric-storage', 2),
  USERS: seedId('metric-users', 3),
  LOANS: seedId('metric-loans', 4),
  DOCUMENTS: seedId('metric-docs', 5),
} as const;

export type MetricId = (typeof METRIC_IDS)[keyof typeof METRIC_IDS];

// =============================================================================
// PAYMENT METHOD IDs
// =============================================================================

export const PAYMENT_METHOD_IDS = {
  // OpsMatrix Capital payment methods
  OPSMATRIX_CARD_VISA: seedId('pm-opsmatrix-visa', 1),
  OPSMATRIX_CARD_AMEX: seedId('pm-opsmatrix-amex', 2),
  OPSMATRIX_BANK_CHASE: seedId('pm-opsmatrix-bank', 3),

  // 1Sharpe Capital payment methods
  SHARPE_CARD_MASTERCARD: seedId('pm-sharpe-mc', 1),

  // Pacific Bridge Fund payment methods
  PACIFIC_CARD_VISA: seedId('pm-pacific-visa', 1),
} as const;

export type PaymentMethodId = (typeof PAYMENT_METHOD_IDS)[keyof typeof PAYMENT_METHOD_IDS];

// =============================================================================
// INVOICE IDs
// =============================================================================

export const INVOICE_IDS = {
  // OpsMatrix Capital invoices
  OPSMATRIX_INV_202601: seedId('inv-opsmatrix-202601', 1),
  OPSMATRIX_INV_202512: seedId('inv-opsmatrix-202512', 2),
  OPSMATRIX_INV_202511: seedId('inv-opsmatrix-202511', 3),

  // 1Sharpe Capital invoices
  SHARPE_INV_202601: seedId('inv-sharpe-202601', 1),

  // Pacific Bridge Fund invoices
  PACIFIC_INV_202601: seedId('inv-pacific-202601', 1),
} as const;

export type InvoiceId = (typeof INVOICE_IDS)[keyof typeof INVOICE_IDS];

// =============================================================================
// PAYMENT IDs
// =============================================================================

export const PAYMENT_IDS = {
  // OpsMatrix Capital payments
  OPSMATRIX_PAY_202601: seedId('pay-opsmatrix-202601', 1),
  OPSMATRIX_PAY_202512: seedId('pay-opsmatrix-202512', 2),
  OPSMATRIX_PAY_202511: seedId('pay-opsmatrix-202511', 3),

  // 1Sharpe Capital payments
  SHARPE_PAY_202601: seedId('pay-sharpe-202601', 1),

  // Pacific Bridge Fund payments
  PACIFIC_PAY_202601: seedId('pay-pacific-202601', 1),
} as const;

export type PaymentId = (typeof PAYMENT_IDS)[keyof typeof PAYMENT_IDS];

// =============================================================================
// SUBSCRIPTION PLAN DATA
// =============================================================================

export interface PlanSeedData {
  id: string;
  name: string;
  description: string;
  /** Price in cents (e.g., 4900 = $49.00) */
  priceInCents: number;
  currency: string;
  interval: 'month' | 'year';
  features: {
    maxUsers: number;
    maxApiCalls: number;
    maxStorageGb: number;
    maxLoans: number;
    supportLevel: 'community' | 'email' | 'priority' | 'dedicated';
    features: string[];
  };
  active: boolean;
}

export const SUBSCRIPTION_PLANS: Record<keyof typeof PLAN_IDS, PlanSeedData> = {
  FREE: {
    id: PLAN_IDS.FREE,
    name: 'Free',
    description: 'Perfect for getting started and small teams',
    priceInCents: 0, // $0.00
    currency: 'USD',
    interval: 'month',
    features: {
      maxUsers: 2,
      maxApiCalls: 1000,
      maxStorageGb: 1,
      maxLoans: 10,
      supportLevel: 'community',
      features: ['Basic loan tracking', 'Single pipeline', 'Community support'],
    },
    active: true,
  },
  STARTER: {
    id: PLAN_IDS.STARTER,
    name: 'Starter',
    description: 'For growing teams and small lenders',
    priceInCents: 4900, // $49.00
    currency: 'USD',
    interval: 'month',
    features: {
      maxUsers: 5,
      maxApiCalls: 10000,
      maxStorageGb: 10,
      maxLoans: 50,
      supportLevel: 'email',
      features: [
        'Advanced loan tracking',
        'Multiple pipelines',
        'Email support',
        'Basic reporting',
        'Document management',
      ],
    },
    active: true,
  },
  PROFESSIONAL: {
    id: PLAN_IDS.PROFESSIONAL,
    name: 'Professional',
    description: 'For established lenders and teams',
    priceInCents: 9900, // $99.00
    currency: 'USD',
    interval: 'month',
    features: {
      maxUsers: 10,
      maxApiCalls: 50000,
      maxStorageGb: 50,
      maxLoans: 200,
      supportLevel: 'priority',
      features: [
        'Full loan lifecycle',
        'Unlimited pipelines',
        'Priority support',
        'Advanced reporting',
        'API access',
        'Custom workflows',
        'Team management',
      ],
    },
    active: true,
  },
  ENTERPRISE: {
    id: PLAN_IDS.ENTERPRISE,
    name: 'Enterprise',
    description: 'For large organizations with custom needs',
    priceInCents: 29900, // $299.00
    currency: 'USD',
    interval: 'month',
    features: {
      maxUsers: -1, // unlimited
      maxApiCalls: 500000,
      maxStorageGb: 500,
      maxLoans: -1, // unlimited
      supportLevel: 'dedicated',
      features: [
        'Everything in Professional',
        'Unlimited users',
        'Unlimited loans',
        'Dedicated support',
        'Custom integrations',
        'SLA guarantee',
        'Advanced security',
        'Audit logs',
        'SSO/SAML',
      ],
    },
    active: true,
  },
};

// =============================================================================
// SUBSCRIPTION DATA
// =============================================================================

export interface SubscriptionSeedData {
  id: string;
  businessId: string;
  planId: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled';
  trialDays?: number;
}

export const SUBSCRIPTIONS: Record<keyof typeof SUBSCRIPTION_IDS, SubscriptionSeedData> = {
  OPSMATRIX_CAPITAL: {
    id: SUBSCRIPTION_IDS.OPSMATRIX_CAPITAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    planId: PLAN_IDS.PROFESSIONAL,
    status: 'active',
  },
  ONE_SHARPE_CAPITAL: {
    id: SUBSCRIPTION_IDS.ONE_SHARPE_CAPITAL,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    planId: PLAN_IDS.PROFESSIONAL,
    status: 'active',
  },
  PACIFIC_BRIDGE_FUND: {
    id: SUBSCRIPTION_IDS.PACIFIC_BRIDGE_FUND,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    planId: PLAN_IDS.STARTER,
    status: 'active',
  },
  GULF_COAST_FO: {
    id: SUBSCRIPTION_IDS.GULF_COAST_FO,
    businessId: BUSINESS_IDS.GULF_COAST_FO,
    planId: PLAN_IDS.STARTER,
    status: 'trialing',
    trialDays: 14,
  },
};

// =============================================================================
// USAGE METRIC DATA
// =============================================================================

export interface MetricSeedData {
  id: string;
  name: string;
  description: string;
  unit: string;
}

export const USAGE_METRICS: Record<keyof typeof METRIC_IDS, MetricSeedData> = {
  API_CALLS: {
    id: METRIC_IDS.API_CALLS,
    name: 'API Calls',
    description: 'Number of API requests made',
    unit: 'requests',
  },
  STORAGE_GB: {
    id: METRIC_IDS.STORAGE_GB,
    name: 'Storage',
    description: 'Total storage used for documents and files',
    unit: 'GB',
  },
  USERS: {
    id: METRIC_IDS.USERS,
    name: 'Active Users',
    description: 'Number of active users in the business',
    unit: 'users',
  },
  LOANS: {
    id: METRIC_IDS.LOANS,
    name: 'Active Loans',
    description: 'Number of active loans being managed',
    unit: 'loans',
  },
  DOCUMENTS: {
    id: METRIC_IDS.DOCUMENTS,
    name: 'Documents',
    description: 'Number of documents uploaded',
    unit: 'documents',
  },
};

// =============================================================================
// PAYMENT METHOD DATA
// =============================================================================

export interface PaymentMethodSeedData {
  id: string;
  businessId: string;
  userId: string;
  type: 'card' | 'bank_account' | 'paypal';
  provider: string;
  providerId: string;
  last4?: string;
  brand?: string;
  expiryMonth?: number;
  expiryYear?: number;
  isDefault: boolean;
  metadata?: Record<string, unknown>;
}

export const PAYMENT_METHODS: Record<keyof typeof PAYMENT_METHOD_IDS, PaymentMethodSeedData> = {
  // OpsMatrix Capital payment methods
  OPSMATRIX_CARD_VISA: {
    id: PAYMENT_METHOD_IDS.OPSMATRIX_CARD_VISA,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    type: 'card',
    provider: 'stripe',
    providerId: 'pm_test_visa_4242',
    last4: '4242',
    brand: 'Visa',
    expiryMonth: 12,
    expiryYear: 2027,
    isDefault: true,
    metadata: { addedBy: 'rachel.martinez@opsmatrix.local' },
  },
  OPSMATRIX_CARD_AMEX: {
    id: PAYMENT_METHOD_IDS.OPSMATRIX_CARD_AMEX,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    type: 'card',
    provider: 'stripe',
    providerId: 'pm_test_amex_0005',
    last4: '0005',
    brand: 'American Express',
    expiryMonth: 6,
    expiryYear: 2028,
    isDefault: false,
    metadata: { addedBy: 'rachel.martinez@opsmatrix.local', purpose: 'backup' },
  },
  OPSMATRIX_BANK_CHASE: {
    id: PAYMENT_METHOD_IDS.OPSMATRIX_BANK_CHASE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    type: 'bank_account',
    provider: 'stripe',
    providerId: 'ba_test_chase_6789',
    last4: '6789',
    brand: 'Chase',
    isDefault: false,
    metadata: { bankName: 'JPMorgan Chase', accountType: 'checking' },
  },

  // 1Sharpe Capital payment methods
  SHARPE_CARD_MASTERCARD: {
    id: PAYMENT_METHOD_IDS.SHARPE_CARD_MASTERCARD,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    userId: USER_IDS.SHARPE_ADMIN,
    type: 'card',
    provider: 'stripe',
    providerId: 'pm_test_mc_5555',
    last4: '5555',
    brand: 'Mastercard',
    expiryMonth: 9,
    expiryYear: 2027,
    isDefault: true,
  },

  // Pacific Bridge Fund payment methods
  PACIFIC_CARD_VISA: {
    id: PAYMENT_METHOD_IDS.PACIFIC_CARD_VISA,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    userId: USER_IDS.PACIFIC_ADMIN,
    type: 'card',
    provider: 'stripe',
    providerId: 'pm_test_visa_1111',
    last4: '1111',
    brand: 'Visa',
    expiryMonth: 3,
    expiryYear: 2028,
    isDefault: true,
  },
};

// =============================================================================
// INVOICE DATA
// =============================================================================

export interface InvoiceItemSeedData {
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

export interface InvoiceSeedData {
  id: string;
  businessId: string;
  subscriptionId?: string;
  invoiceNumber: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  subtotal: number;
  tax: number;
  total: number;
  currency: string;
  dueDate: string;
  paidAt?: string;
  items: InvoiceItemSeedData[];
  metadata?: Record<string, unknown>;
}

export const INVOICES: Record<keyof typeof INVOICE_IDS, InvoiceSeedData> = {
  // OpsMatrix Capital invoices
  OPSMATRIX_INV_202601: {
    id: INVOICE_IDS.OPSMATRIX_INV_202601,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    subscriptionId: SUBSCRIPTION_IDS.OPSMATRIX_CAPITAL,
    invoiceNumber: 'INV-2026-0001',
    status: 'open',
    subtotal: 99.0,
    tax: 0,
    total: 99.0,
    currency: 'USD',
    dueDate: '2026-02-01',
    items: [{ description: 'Professional Plan - January 2026', quantity: 1, unitPrice: 99.0, amount: 99.0 }],
  },
  OPSMATRIX_INV_202512: {
    id: INVOICE_IDS.OPSMATRIX_INV_202512,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    subscriptionId: SUBSCRIPTION_IDS.OPSMATRIX_CAPITAL,
    invoiceNumber: 'INV-2025-0012',
    status: 'paid',
    subtotal: 99.0,
    tax: 0,
    total: 99.0,
    currency: 'USD',
    dueDate: '2026-01-01',
    paidAt: '2025-12-28',
    items: [{ description: 'Professional Plan - December 2025', quantity: 1, unitPrice: 99.0, amount: 99.0 }],
  },
  OPSMATRIX_INV_202511: {
    id: INVOICE_IDS.OPSMATRIX_INV_202511,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    subscriptionId: SUBSCRIPTION_IDS.OPSMATRIX_CAPITAL,
    invoiceNumber: 'INV-2025-0011',
    status: 'paid',
    subtotal: 99.0,
    tax: 0,
    total: 99.0,
    currency: 'USD',
    dueDate: '2025-12-01',
    paidAt: '2025-11-29',
    items: [{ description: 'Professional Plan - November 2025', quantity: 1, unitPrice: 99.0, amount: 99.0 }],
  },

  // 1Sharpe Capital invoices
  SHARPE_INV_202601: {
    id: INVOICE_IDS.SHARPE_INV_202601,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    subscriptionId: SUBSCRIPTION_IDS.ONE_SHARPE_CAPITAL,
    invoiceNumber: 'INV-2026-0002',
    status: 'paid',
    subtotal: 99.0,
    tax: 7.92,
    total: 106.92,
    currency: 'USD',
    dueDate: '2026-02-01',
    paidAt: '2026-01-15',
    items: [{ description: 'Professional Plan - January 2026', quantity: 1, unitPrice: 99.0, amount: 99.0 }],
    metadata: { taxJurisdiction: 'GA', taxRate: 0.08 },
  },

  // Pacific Bridge Fund invoices
  PACIFIC_INV_202601: {
    id: INVOICE_IDS.PACIFIC_INV_202601,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    subscriptionId: SUBSCRIPTION_IDS.PACIFIC_BRIDGE_FUND,
    invoiceNumber: 'INV-2026-0003',
    status: 'open',
    subtotal: 49.0,
    tax: 4.04,
    total: 53.04,
    currency: 'USD',
    dueDate: '2026-02-01',
    items: [{ description: 'Starter Plan - January 2026', quantity: 1, unitPrice: 49.0, amount: 49.0 }],
    metadata: { taxJurisdiction: 'CA', taxRate: 0.0825 },
  },
};

// =============================================================================
// PAYMENT DATA
// =============================================================================

export interface PaymentSeedData {
  id: string;
  businessId: string;
  subscriptionId?: string;
  invoiceId?: string;
  paymentMethodId?: string;
  amount: number;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed' | 'refunded';
  provider: string;
  providerId?: string;
  failureReason?: string;
  paidAt?: string;
  metadata?: Record<string, unknown>;
}

export const PAYMENTS: Record<keyof typeof PAYMENT_IDS, PaymentSeedData> = {
  // OpsMatrix Capital payments
  OPSMATRIX_PAY_202601: {
    id: PAYMENT_IDS.OPSMATRIX_PAY_202601,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    subscriptionId: SUBSCRIPTION_IDS.OPSMATRIX_CAPITAL,
    invoiceId: INVOICE_IDS.OPSMATRIX_INV_202601,
    paymentMethodId: PAYMENT_METHOD_IDS.OPSMATRIX_CARD_VISA,
    amount: 99.0,
    currency: 'USD',
    status: 'pending',
    provider: 'stripe',
    metadata: { scheduledFor: '2026-02-01' },
  },
  OPSMATRIX_PAY_202512: {
    id: PAYMENT_IDS.OPSMATRIX_PAY_202512,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    subscriptionId: SUBSCRIPTION_IDS.OPSMATRIX_CAPITAL,
    invoiceId: INVOICE_IDS.OPSMATRIX_INV_202512,
    paymentMethodId: PAYMENT_METHOD_IDS.OPSMATRIX_CARD_VISA,
    amount: 99.0,
    currency: 'USD',
    status: 'succeeded',
    provider: 'stripe',
    providerId: 'pi_test_123456789012',
    paidAt: '2025-12-28',
  },
  OPSMATRIX_PAY_202511: {
    id: PAYMENT_IDS.OPSMATRIX_PAY_202511,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    subscriptionId: SUBSCRIPTION_IDS.OPSMATRIX_CAPITAL,
    invoiceId: INVOICE_IDS.OPSMATRIX_INV_202511,
    paymentMethodId: PAYMENT_METHOD_IDS.OPSMATRIX_CARD_VISA,
    amount: 99.0,
    currency: 'USD',
    status: 'succeeded',
    provider: 'stripe',
    providerId: 'pi_test_098765432109',
    paidAt: '2025-11-29',
  },

  // 1Sharpe Capital payments
  SHARPE_PAY_202601: {
    id: PAYMENT_IDS.SHARPE_PAY_202601,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    subscriptionId: SUBSCRIPTION_IDS.ONE_SHARPE_CAPITAL,
    invoiceId: INVOICE_IDS.SHARPE_INV_202601,
    paymentMethodId: PAYMENT_METHOD_IDS.SHARPE_CARD_MASTERCARD,
    amount: 106.92,
    currency: 'USD',
    status: 'succeeded',
    provider: 'stripe',
    providerId: 'pi_test_sharpe_202601',
    paidAt: '2026-01-15',
  },

  // Pacific Bridge Fund payments
  PACIFIC_PAY_202601: {
    id: PAYMENT_IDS.PACIFIC_PAY_202601,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    subscriptionId: SUBSCRIPTION_IDS.PACIFIC_BRIDGE_FUND,
    invoiceId: INVOICE_IDS.PACIFIC_INV_202601,
    paymentMethodId: PAYMENT_METHOD_IDS.PACIFIC_CARD_VISA,
    amount: 53.04,
    currency: 'USD',
    status: 'pending',
    provider: 'stripe',
    metadata: { scheduledFor: '2026-02-01' },
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all subscription plans as an array
 */
export function getAllPlans(): PlanSeedData[] {
  return Object.values(SUBSCRIPTION_PLANS);
}

/**
 * Get all subscriptions as an array
 */
export function getAllSubscriptions(): SubscriptionSeedData[] {
  return Object.values(SUBSCRIPTIONS);
}

/**
 * Get all usage metrics as an array
 */
export function getAllMetrics(): MetricSeedData[] {
  return Object.values(USAGE_METRICS);
}

/**
 * Get subscription by business ID
 */
export function getSubscriptionByBusiness(businessId: string): SubscriptionSeedData | undefined {
  return Object.values(SUBSCRIPTIONS).find((s) => s.businessId === businessId);
}

/**
 * Get plan by ID
 */
export function getPlanById(planId: string): PlanSeedData | undefined {
  return Object.values(SUBSCRIPTION_PLANS).find((p) => p.id === planId);
}

/**
 * Get all payment methods as an array
 */
export function getAllPaymentMethods(): PaymentMethodSeedData[] {
  return Object.values(PAYMENT_METHODS);
}

/**
 * Get payment methods by business ID
 */
export function getPaymentMethodsByBusiness(businessId: string): PaymentMethodSeedData[] {
  return Object.values(PAYMENT_METHODS).filter((pm) => pm.businessId === businessId);
}

/**
 * Get default payment method for a business
 */
export function getDefaultPaymentMethod(businessId: string): PaymentMethodSeedData | undefined {
  return Object.values(PAYMENT_METHODS).find((pm) => pm.businessId === businessId && pm.isDefault);
}

/**
 * Get all invoices as an array
 */
export function getAllInvoices(): InvoiceSeedData[] {
  return Object.values(INVOICES);
}

/**
 * Get invoices by business ID
 */
export function getInvoicesByBusiness(businessId: string): InvoiceSeedData[] {
  return Object.values(INVOICES).filter((inv) => inv.businessId === businessId);
}

/**
 * Get invoices by status
 */
export function getInvoicesByStatus(status: InvoiceSeedData['status']): InvoiceSeedData[] {
  return Object.values(INVOICES).filter((inv) => inv.status === status);
}

/**
 * Get all payments as an array
 */
export function getAllPayments(): PaymentSeedData[] {
  return Object.values(PAYMENTS);
}

/**
 * Get payments by business ID
 */
export function getPaymentsByBusiness(businessId: string): PaymentSeedData[] {
  return Object.values(PAYMENTS).filter((pay) => pay.businessId === businessId);
}

/**
 * Get payments by status
 */
export function getPaymentsByStatus(status: PaymentSeedData['status']): PaymentSeedData[] {
  return Object.values(PAYMENTS).filter((pay) => pay.status === status);
}

/**
 * Get payments by invoice ID
 */
export function getPaymentsByInvoice(invoiceId: string): PaymentSeedData[] {
  return Object.values(PAYMENTS).filter((pay) => pay.invoiceId === invoiceId);
}
